import { memo } from 'react'
import { Node } from 'reactflow'
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select"
import { Switch } from "@/shared/components/ui/switch"
import { Slider } from "@/shared/components/ui/slider"
import { Badge } from "@/shared/components/ui/badge"
import { Loader2, Volume2 } from "lucide-react"
import { useVoiceConfig } from "@/modules/agent/hooks/useVoiceConfig"
import { VoicePicker } from "@/modules/agent/components/VoicePicker"

interface OutputNodeConfigProps {
  selectedNode: Node
  onUpdateNodeData: (field: string, value: unknown) => void
}

export const OutputNodeConfig = memo(({ 
  selectedNode, 
  onUpdateNodeData 
}: OutputNodeConfigProps) => {
  // 加载语音配置
  const { 
    ttsProviders, 
    defaultTtsProvider, 
    audioFormats,
    getVoicesByProvider,
    getDefaultVoiceId,
    getDefaultAudioFormat,
    isLoading: isLoadingVoiceConfig 
  } = useVoiceConfig()
  
  // 当前选择的 TTS 提供商
  const currentProvider = selectedNode.data.ttsProvider || defaultTtsProvider || 'cosyvoice'
  // 获取当前提供商的音色列表
  const currentVoices = getVoicesByProvider(currentProvider)

  return (
    <>
      <div className="space-y-2">
        <Label>出口名称</Label>
        <Input
          value={selectedNode.data.label || ''}
          onChange={(e) => onUpdateNodeData('label', e.target.value)}
          placeholder="输出节点名称"
        />
      </div>

      <div className="space-y-2">
        <Label>输出类型</Label>
        <Select
          value={selectedNode.data.outputType || 'reply'}
          onValueChange={(value) => onUpdateNodeData('outputType', value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="选择输出类型" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="reply">回复用户</SelectItem>
            <SelectItem value="silent">静默结束</SelectItem>
            <SelectItem value="webhook">触发 Webhook</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* V6.0: TTS 语音输出配置 */}
      <div className="pt-3 mt-3 border-t space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Volume2 className="h-4 w-4 text-green-500" />
            <Label htmlFor="enable-tts-switch" className="text-sm font-medium">
              语音输出
            </Label>
          </div>
          <Switch
            id="enable-tts-switch"
            checked={selectedNode.data.enableTTS ?? false}
            onCheckedChange={(checked) => {
              onUpdateNodeData('enableTTS', checked)
              // 启用 TTS 时，确保设置默认的音频格式（避免 undefined 导致后端使用 PCM）
              if (checked && !selectedNode.data.audioFormat) {
                onUpdateNodeData('audioFormat', getDefaultAudioFormat())
              }
            }}
          />
        </div>
        <p className="text-xs text-slate-500">
          开启后 Agent 回复将转换为语音播放
        </p>
        
        {/* TTS 详细配置（仅当语音输出开启时显示） */}
        {selectedNode.data.enableTTS && (
          <div className="space-y-3 pt-2">
            {/* TTS 提供商 */}
            <div className="space-y-2">
              <Label className="text-sm">语音合成服务</Label>
              {isLoadingVoiceConfig ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  加载中...
                </div>
              ) : (
                <Select
                  value={currentProvider}
                  onValueChange={(value) => {
                    onUpdateNodeData('ttsProvider', value)
                    // 切换提供商时，重置音色为新提供商的默认音色
                    const newDefaultVoice = getDefaultVoiceId(value)
                    if (newDefaultVoice) {
                      onUpdateNodeData('defaultVoiceId', newDefaultVoice)
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="选择 TTS 服务" />
                  </SelectTrigger>
                  <SelectContent>
                    {ttsProviders.map((provider) => (
                      <SelectItem key={provider.id} value={provider.id}>
                        <div className="flex items-center gap-2">
                          <span>{provider.name}</span>
                          {provider.default && (
                            <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">
                              默认
                            </Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            
            {/* 自定义 TTS URL（仅当选择 custom 时显示） */}
            {currentProvider === 'custom' && (
              <div className="space-y-2">
                <Label className="text-sm">自定义 TTS URL</Label>
                <Input
                  value={selectedNode.data.customTtsUrl || ''}
                  onChange={(e) => onUpdateNodeData('customTtsUrl', e.target.value)}
                  placeholder="wss://your-tts-service.com/ws"
                />
                <p className="text-xs text-slate-500">
                  WebSocket 地址，需兼容 Axiom TTS 协议
                </p>
              </div>
            )}
            
            {/* 音色选择（非 custom 时显示） */}
            {currentProvider !== 'custom' && currentVoices.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm">默认音色</Label>
                <VoicePicker
                  value={selectedNode.data.defaultVoiceId || getDefaultVoiceId(currentProvider)}
                  onValueChange={(value) => onUpdateNodeData('defaultVoiceId', value)}
                  voices={currentVoices}
                  placeholder="选择音色"
                />
                <p className="text-xs text-slate-500">
                  Agent 可在回复中覆盖此音色
                </p>
              </div>
            )}
            
            {/* 语速调节 */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">语速</Label>
                <span className="text-xs text-slate-500">
                  {(selectedNode.data.speed ?? 1.0).toFixed(1)}x
                </span>
              </div>
              <Slider
                value={selectedNode.data.speed ?? 1.0}
                onValueChange={(value) => onUpdateNodeData('speed', value)}
                min={0.5}
                max={2.0}
                step={0.1}
              />
              <div className="flex justify-between text-xs text-slate-400">
                <span>0.5x</span>
                <span>1.0x</span>
                <span>2.0x</span>
              </div>
            </div>
            
            {/* 音频格式 */}
            <div className="space-y-2">
              <Label className="text-sm">音频格式</Label>
              <Select
                value={selectedNode.data.audioFormat || getDefaultAudioFormat()}
                onValueChange={(value) => onUpdateNodeData('audioFormat', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="选择格式" />
                </SelectTrigger>
                <SelectContent>
                  {audioFormats.map((format) => (
                    <SelectItem key={format.id} value={format.id}>
                      <div className="flex items-center gap-2">
                        <span>{format.name}</span>
                        {format.default && (
                          <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">
                            默认
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* 采样率 */}
            <div className="space-y-2">
              <Label className="text-sm">采样率</Label>
              <Select
                value={String(selectedNode.data.sampleRate || 22050)}
                onValueChange={(value) => onUpdateNodeData('sampleRate', parseInt(value, 10))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="选择采样率" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="8000">8000 Hz (电话质量)</SelectItem>
                  <SelectItem value="16000">16000 Hz (宽带语音)</SelectItem>
                  <SelectItem value="22050">
                    <div className="flex items-center gap-2">
                      <span>22050 Hz</span>
                      <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">默认</Badge>
                    </div>
                  </SelectItem>
                  <SelectItem value="24000">24000 Hz (Opus 原生)</SelectItem>
                  <SelectItem value="44100">44100 Hz (CD 质量)</SelectItem>
                  <SelectItem value="48000">48000 Hz (专业音频)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                {selectedNode.data.audioFormat === 'opus' 
                  ? 'Opus 格式会自动匹配最接近的支持采样率' 
                  : '更高采样率提供更好音质，但增加网络传输量'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* v12.0: 情绪学习配置 */}
      <div className="pt-3 mt-3 border-t space-y-3">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label className="text-sm">情绪学习</Label>
            <p className="text-xs text-slate-500">对话结束后分析用户情绪状态并记录</p>
          </div>
          <Switch
            checked={selectedNode.data.enable_mood_learning ?? false}
            onCheckedChange={(checked) => onUpdateNodeData('enable_mood_learning', checked)}
          />
        </div>
      </div>

      <div className="pt-4 border-t border-slate-200">
        <div className="text-xs text-slate-500">
          <p className="mb-1">💡 使用提示：</p>
          <ul className="space-y-1 text-xs">
            <li>• 输出节点是流程的终点，用于展示最终结果</li>
            <li>• 开启语音输出后，文本回复会自动转换为语音</li>
            <li>• 采样率影响音质和文件大小，建议使用默认值</li>
            <li>• 情绪学习可帮助系统更好地理解用户状态</li>
          </ul>
        </div>
      </div>
    </>
  )
})

OutputNodeConfig.displayName = 'OutputNodeConfig'